import React, {memo, useCallback, useEffect, useMemo, useState, type ReactNode} from 'react'
import {MessagePrimitive} from '@assistant-ui/react'
import type {MessagePrimitiveGroupedParts} from '@assistant-ui/core/react'
import type {ReasoningMessagePartProps, TextMessagePartProps, ToolCallMessagePartProps} from '@assistant-ui/react'
import {useMessage} from '@assistant-ui/react'

import {ChatPanel} from './components/ChatPanel'
import {getOmicsAssistantMessage} from './getOmicsAssistantMessage'
import {OmicsAssistantTextPart} from './omicsAssistantMarkdown'
import {OmicsCoTReasoningChunk} from './omicsCoTReasoningChunk'
import {OmicsAssistantToolFallback} from './omicsAssistantToolPrimitives'
import {omicsCoTGroupBy, type CoTGroupKey} from './omicsCoTGroupBy'
import {countWorkingToolSteps, formatWorkingFailMeta, formatWorkingTitle} from './workingSummary'

export {omicsCoTGroupBy} from './omicsCoTGroupBy'

const OmicsChainOfThoughtShell: React.FC<{
  status: MessagePrimitiveGroupedParts.GroupPart<CoTGroupKey>['status']
  children: ReactNode
}> = memo(({status, children}) => {
  const omics = useMessage((s) => getOmicsAssistantMessage(s))
  const running =
    status.type === 'running' ||
    status.type === 'requires-action' ||
    Boolean(omics?.isStreamingTurn)
  // Closed in a saved conversation; opens while the turn runs and then keeps the user's choice.
  const [open, setOpen] = useState(running)

  useEffect(() => {
    if (running) setOpen(true)
  }, [running])

  const toggle = useCallback(() => setOpen((o) => !o), [])
  const counts = useMemo(() => countWorkingToolSteps(omics), [omics])
  const failMeta = formatWorkingFailMeta(counts.failed)

  return (
    <ChatPanel
      icon='fa-screwdriver-wrench'
      title={formatWorkingTitle(counts.total)}
      meta={failMeta || undefined}
      open={open}
      onToggle={toggle}
      className='ai-chat-working-strip omics-cot-working'
      detailsClassName='ai-chat-working-details omics-cot-working-details'
    >
      {children}
    </ChatPanel>
  )
})

OmicsChainOfThoughtShell.displayName = 'OmicsChainOfThoughtShell'

const OmicsCoTReasoningSection: React.FC<{children: ReactNode}> = memo(({children}) => (
  <div className='omics-cot-reasoning-section'>{children}</div>
))

OmicsCoTReasoningSection.displayName = 'OmicsCoTReasoningSection'

const OmicsCoTToolSection: React.FC<{children: ReactNode}> = memo(({children}) => (
  <div className='omics-cot-tool-section'>{children}</div>
))

OmicsCoTToolSection.displayName = 'OmicsCoTToolSection'

function renderGroupedPart(info: MessagePrimitiveGroupedParts.RenderInfo<CoTGroupKey>): ReactNode {
  const {part, children} = info

  switch (part.type) {
    case 'group-chainOfThought':
      return (
        <OmicsChainOfThoughtShell status={part.status}>{children}</OmicsChainOfThoughtShell>
      )
    case 'group-reasoning':
      return <OmicsCoTReasoningSection>{children}</OmicsCoTReasoningSection>
    case 'group-tool':
      return <OmicsCoTToolSection>{children}</OmicsCoTToolSection>
    case 'text':
      return <OmicsAssistantTextPart {...(part as TextMessagePartProps)} />
    case 'reasoning': {
      const reasoningPart = part as ReasoningMessagePartProps
      return <OmicsCoTReasoningChunk text={reasoningPart.text} />
    }
    case 'tool-call': {
      const toolPart = part as ToolCallMessagePartProps & {toolUI?: ReactNode}
      return toolPart.toolUI ?? <OmicsAssistantToolFallback {...toolPart} />
    }
    case 'data':
      return null
    default:
      return null
  }
}

type GroupedPartsProps = MessagePrimitiveGroupedParts.Props<CoTGroupKey>

const GroupedParts = MessagePrimitive.GroupedParts as React.FC<GroupedPartsProps>

/** assistant-ui GroupedParts: preamble/answer text + Chain of Thought Working strip. */
export const OmicsAssistantGroupedParts: React.FC = memo(() => (
  <GroupedParts groupBy={omicsCoTGroupBy}>{renderGroupedPart}</GroupedParts>
))

OmicsAssistantGroupedParts.displayName = 'OmicsAssistantGroupedParts'
